import { useIntl } from "react-intl";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import {
  MISSING_POWER_LABEL_IDS,
  MissingPowerScore,
  POWER_SCORE_LENS_LABEL_IDS,
  PowerScoreLens,
} from "@/lib/score-source";
import type { MatchData } from "./match-design-types";

type PowerScoreData = Pick<MatchData, "powerScoreLens" | "missingPowerScore">;

export function PowerScoreSettings({
  data,
  update,
  readOnly,
}: {
  data: PowerScoreData;
  update: (patch: Partial<MatchData>) => void;
  readOnly: boolean;
}) {
  const intl = useIntl();

  // DIESELBEN SCHLUESSEL WIE DIE POWER MAP, nicht bloss derselbe Wortlaut. Die Power Map
  // (PowerLibraryPage) und die Powers-Box im Profil fuehren genau diese drei Linsen schon, und
  // lib/score-source.ts sagt warum: "so both mean the same thing by the same word". Eine eigene
  // Kopie im match.*-Namensraum waere die dritte Stelle, an der dasselbe Wort gepflegt werden
  // muss — und die erste, die abweicht. Die Reihenfolge ist auch die der Power Map.
  const lensLabels: Record<PowerScoreLens, string> = {
    SELF_ASSESSMENT: intl.formatMessage({ id: POWER_SCORE_LENS_LABEL_IDS.SELF_ASSESSMENT }),
    SMART_ANALYSIS: intl.formatMessage({ id: POWER_SCORE_LENS_LABEL_IDS.SMART_ANALYSIS }),
    COMBINED: intl.formatMessage({ id: POWER_SCORE_LENS_LABEL_IDS.COMBINED }),
  };
  // Eigenes Label statt des Power-Map-Titels: die Karte heisst bereits "Power Scorecard", ein
  // Regler namens "Power Score" wiederholte sie nur. Die drei OPTIONEN bleiben auf den geteilten
  // Grow-Ids — das ist das Vokabular, das gleich bleiben muss, nicht die Beschriftung.
  const lensLabel = intl.formatMessage({
    id: "match.powerScore.source",
    defaultMessage: "Power-Quelle",
  });
  const missingLabel = intl.formatMessage({
    id: "match.powerScore.missing",
    defaultMessage: "Fehlende Power",
  });
  // Ueber dieselbe Map wie der Ausloeser in der Karte: ein Wort je Zustand.
  const missingLabels: Record<MissingPowerScore, string> = {
    ZERO: intl.formatMessage({ id: MISSING_POWER_LABEL_IDS.ZERO, defaultMessage: "zählt 0" }),
    NEUTRAL: intl.formatMessage({ id: MISSING_POWER_LABEL_IDS.NEUTRAL, defaultMessage: "zählt 50" }),
  };

  return (
    // EINE ZEILE JE EINSTELLUNG, wie die Power Map dieselbe Linse zeigt (PowerLibraryPage:
    // smycles-field-label + SegmentedControl in einer flex-Reihe). Gestapelte Labels machten aus zwei
    // Qualifizierern vier Zeilen und damit das lauteste Element einer Karte, die sonst aus
    // Haarlinien-Balken besteht — die Gewichte sind hier die Hauptsache, nicht diese beiden.
    // Die Zwei-Spalten-Mechanik ist raus: bei der Breite dieser Karte griff sie nie.
    <div data-testid="power-score-settings">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="smycles-field-label min-w-[7.5rem]">{lensLabel}</span>
        <SegmentedControl
          id="match-power-source"
          value={data.powerScoreLens}
          onChange={(powerScoreLens) => update({ powerScoreLens })}
          size="sm"
          optionClassName="whitespace-nowrap"
          options={PowerScoreLens.options.map((value) => ({
            value,
            disabled: readOnly,
            label: lensLabels[value],
          }))}
          aria-label={lensLabel}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="smycles-field-label min-w-[7.5rem]">{missingLabel}</span>
        <SegmentedControl
          value={data.missingPowerScore}
          onChange={(missingPowerScore) => update({ missingPowerScore })}
          size="sm"
          optionClassName="whitespace-nowrap"
          options={MissingPowerScore.options.map((value) => ({
            value,
            disabled: readOnly,
            label: missingLabels[value],
          }))}
          aria-label={intl.formatMessage({
            id: "match.powerScore.missing",
            defaultMessage: "Fehlende Power",
          })}
        />
        </div>
      </div>
    </div>
  );
}
